import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Check, Copy, Download, KeyRound, ShieldCheck, Smartphone } from 'lucide-react';
import { api, ApiError, type Realm } from '@/lib/api';
import { Button } from '@/ui/button';
import { Input, Field } from '@/ui/input';
import { MicroLabel } from '@/ui/micro-label';
import { Wordmark } from '@/domain/wordmark';

/**
 * The MFA route tree, shared by both realms.
 *
 * These six screens were the `Stub` component for as long as the API was issuing MFA-verified
 * sessions to unenrolled accounts — the two gaps hid each other, because nothing ever reached a
 * challenge to notice the screen was empty. With the server no longer inferring the fact
 * (INV-83), these are the only way in.
 *
 * Everything rendered here comes from `GET /me` or from the response to the call just made. The
 * client decides nothing about enrolment state: that is the rule frontend.md section 3 is about,
 * and it is why the guard, not these screens, chooses which one you see.
 *
 * No secret is written to storage. The provisioning URI and the backup codes exist in component
 * state for as long as the screen is mounted and nowhere else — a `localStorage` copy would
 * outlive the session and end up in a backup of the user's browser profile.
 */

/* ------------------------------------------------------------------ chrome */

function MfaCard({
  title,
  intro,
  icon,
  children,
  realm
}: {
  title: string;
  intro: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  realm: Realm;
}) {
  return (
    <div className="grid min-h-screen place-items-center bg-background px-4 py-12">
      <div className="w-full max-w-[420px]">
        <div className="grid justify-items-center gap-3">
          <Wordmark height={16} />
          <span className="rounded-[3px] border border-border px-2 py-1 font-mono text-[10px] tracking-[0.12em] text-muted uppercase">
            {realm === 'admin' ? 'Operator console' : 'Vendor portal'}
          </span>
        </div>
        <div className="mt-8 grid gap-5 rounded border border-border bg-card px-8 py-7">
          <div className="grid gap-1.5">
            <div className="flex items-center gap-2 text-muted">{icon}</div>
            <h1 className="text-[22px] font-medium text-foreground">{title}</h1>
            <p className="text-[13px] leading-relaxed text-muted">{intro}</p>
          </div>
          {children}
        </div>
        <SignOut realm={realm} />
      </div>
    </div>
  );
}

/**
 * Present on every screen in this tree, and not decoration.
 *
 * Someone who cannot complete a challenge — wrong phone, no backup codes to hand — would otherwise
 * be stuck on a page with no exit: the guard sends them back here from anywhere else, and the
 * session cookie survives a browser restart. Signing out is the one action that always works.
 */
function SignOut({ realm }: { realm: Realm }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="mt-6 block w-full text-center text-[11px] text-muted underline-offset-2 hover:underline"
      onClick={async () => {
        await api('/logout', { method: 'POST', realm }).catch(() => {});
        await queryClient.invalidateQueries({ queryKey: ['profile'] });
        navigate(realm === 'admin' ? '/admin/login' : '/v/login', { replace: true });
      }}
    >
      Sign out
    </button>
  );
}

/** A six-digit field. `inputMode` and `autoComplete` are what make a phone offer the code. */
function CodeField({
  value,
  onChange,
  label = 'Authentication code',
  id = 'code',
  autoFocus
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  id?: string;
  autoFocus?: boolean;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <Input
        id={id}
        value={value}
        onChange={e => onChange(e.target.value.trim())}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="000000"
        className="text-center font-mono text-lg tracking-[0.4em]"
        autoFocus={autoFocus}
        required
      />
    </Field>
  );
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

/* ------------------------------------------------------------------ setup */

interface SetupResponse {
  secret: string;
  provisioning_uri: string;
}

export function MfaSetup({ realm }: { realm: Realm }) {
  const queryClient = useQueryClient();
  const [enrolment, setEnrolment] = useState<SetupResponse | null>(null);
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const { busy, error, run } = useSubmit();
  const started = useRef(false);

  useEffect(() => {
    // Guarded because React 18+ mounts effects twice in development, and each call issues a *new*
    // pending secret — the second would silently invalidate the QR the user had just scanned.
    if (started.current) return;
    started.current = true;
    void run(async () => {
      const result = await api<SetupResponse>('/mfa/setup', { method: 'POST', realm });
      setEnrolment(result);
      setQr(await QRCode.toDataURL(result.provisioning_uri, { margin: 1, width: 320 }));
    });
  }, []);

  const grouped = useMemo(() => (enrolment?.secret ?? '').replace(/(.{4})/g, '$1 ').trim(), [enrolment]);

  return (
    <MfaCard
      realm={realm}
      icon={<Smartphone className="size-4" />}
      title="Set up two-factor authentication"
      intro="Scan this with an authenticator app — 1Password, Authy, Google Authenticator, or any other TOTP app — then enter the six-digit code it shows."
    >
      {enrolment ? (
        <>
          <div className="grid justify-items-center gap-3 rounded border border-border bg-inset px-4 py-5">
            {qr ? (
              <img src={qr} alt="Enrolment QR code" width={200} height={200} className="rounded bg-white p-2" />
            ) : (
              <div className="size-[200px] animate-pulse rounded bg-border/40" />
            )}
            <div className="grid justify-items-center gap-1.5">
              <MicroLabel size="xs">Or enter this key by hand</MicroLabel>
              <CopyableText value={enrolment.secret} display={grouped} />
            </div>
          </div>

          <form
            className="grid gap-4"
            onSubmit={e => {
              e.preventDefault();
              void run(async () => {
                await api('/mfa/confirm', { method: 'POST', realm, body: JSON.stringify({ code }) });
                // The guard reads /me and moves us on to the backup codes; it is not this screen's
                // decision where to go next.
                await queryClient.invalidateQueries({ queryKey: ['profile'] });
              });
            }}
          >
            <CodeField value={code} onChange={setCode} />
            {error ? <p className="text-[13px] text-danger">{error}</p> : null}
            <Button type="submit" disabled={busy || code.length < 6} className="w-full">
              {busy ? 'Verifying…' : 'Verify and continue'}
            </Button>
          </form>
        </>
      ) : error ? (
        <p className="text-[13px] text-danger">{error}</p>
      ) : (
        <p className="text-[13px] text-muted">Preparing your enrolment…</p>
      )}
    </MfaCard>
  );
}

function CopyableText({ value, display }: { value: string; display?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="flex items-center gap-2 rounded border border-border px-2.5 py-1.5 font-mono text-[12px] tracking-wider text-foreground hover:border-primary"
      onClick={async () => {
        await navigator.clipboard.writeText(value).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {display ?? value}
      {copied ? <Check className="size-3.5 text-pass" /> : <Copy className="size-3.5 text-muted" />}
    </button>
  );
}

/* -------------------------------------------------------------- challenge */

export function MfaChallenge({ realm }: { realm: Realm }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const { busy, error, run } = useSubmit();
  // 429 is its own state: the account is locked and no code will work until the window passes.
  const [locked, setLocked] = useState(false);

  return (
    <MfaCard
      realm={realm}
      icon={<ShieldCheck className="size-4" />}
      title="Two-factor authentication"
      intro={
        useBackup
          ? 'Enter one of the backup codes you saved when you enrolled. Each one works once.'
          : 'Enter the six-digit code from your authenticator app.'
      }
    >
      {locked ? (
        <div className="grid gap-3">
          <p className="rounded border border-danger/40 bg-danger/5 px-3 py-2.5 text-[13px] text-danger">
            Too many incorrect codes. This account is locked for 15 minutes. Wait for the window to pass, or use a
            backup code once it has.
          </p>
          <Button variant="secondary" onClick={() => setLocked(false)} className="w-full">
            Try again
          </Button>
        </div>
      ) : (
        <form
          className="grid gap-4"
          onSubmit={e => {
            e.preventDefault();
            void run(async () => {
              try {
                await api('/mfa/challenge', { method: 'POST', realm, body: JSON.stringify({ code }) });
              } catch (cause) {
                if (cause instanceof ApiError && cause.status === 429) setLocked(true);
                throw cause;
              }
              await queryClient.invalidateQueries({ queryKey: ['profile'] });
            });
          }}
        >
          {useBackup ? (
            <Field label="Backup code" htmlFor="code">
              <Input
                id="code"
                value={code}
                onChange={e => setCode(e.target.value.trim())}
                autoComplete="one-time-code"
                placeholder="0123456789abcdef0123"
                className="text-center font-mono tracking-[0.2em]"
                autoFocus
                required
              />
            </Field>
          ) : (
            <CodeField value={code} onChange={setCode} autoFocus />
          )}

          {error ? <p className="text-[13px] text-danger">{error}</p> : null}

          <Button type="submit" disabled={busy || !code} className="w-full">
            {busy ? 'Verifying…' : 'Verify'}
          </Button>

          <button
            type="button"
            className="text-center text-[12px] text-muted underline-offset-2 hover:underline"
            onClick={() => {
              setUseBackup(v => !v);
              setCode('');
            }}
          >
            {useBackup ? 'Use your authenticator app instead' : 'Use a backup code instead'}
          </button>

          {realm === 'vendor' ? (
            <p className="text-center text-[11px] leading-relaxed text-muted">
              Lost your device and your backup codes?{' '}
              <a href="/v/mfa/recover" className="underline underline-offset-2">
                Request help from an operator
              </a>
              .
            </p>
          ) : (
            <p className="text-center text-[11px] leading-relaxed text-muted">
              Lost your device? Another super admin can reset your second factor.
            </p>
          )}
        </form>
      )}
    </MfaCard>
  );
}

/* ----------------------------------------------------------- backup codes */

/**
 * Shown once, and the acknowledgement is what ends the enrolment.
 *
 * The codes arrive in the response to this request and are never fetchable again — the server keeps
 * only their digests. So the button that leaves this screen is deliberately gated on an explicit
 * checkbox rather than being a plain "continue": the whole point of the screen is that the user
 * cannot come back to it.
 */
export function MfaBackupCodes({ realm }: { realm: Realm }) {
  const queryClient = useQueryClient();
  const [codes, setCodes] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const { busy, error, run } = useSubmit();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void run(async () => {
      const result = await api<{ backup_codes: string[] }>('/mfa/backup-codes/regenerate', {
        method: 'POST',
        realm
      });
      setCodes(result.backup_codes);
    });
  }, []);

  const asText = codes.join('\n');

  return (
    <MfaCard
      realm={realm}
      icon={<KeyRound className="size-4" />}
      title="Save your backup codes"
      intro="Each code signs you in once if you lose your authenticator. Store them somewhere you can reach without this account — a password manager, or printed."
    >
      {codes.length ? (
        <>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-2 rounded border border-border bg-inset px-4 py-4 font-mono text-[13px] text-foreground">
            {codes.map(code => (
              <li key={code} className="tracking-wider">
                {code}
              </li>
            ))}
          </ul>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={async () => {
                await navigator.clipboard.writeText(asText).catch(() => {});
              }}
            >
              <Copy className="size-3.5" /> Copy
            </Button>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                const url = URL.createObjectURL(new Blob([asText], { type: 'text/plain' }));
                const link = document.createElement('a');
                link.href = url;
                link.download = 'backup-codes.txt';
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download className="size-3.5" /> Download
            </Button>
          </div>

          <label className="flex items-start gap-2.5 text-[13px] leading-relaxed text-muted">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className="mt-0.5 size-3.5 accent-[var(--primary)]"
            />
            I have saved these codes somewhere I can reach without this account.
          </label>

          {error ? <p className="text-[13px] text-danger">{error}</p> : null}

          <Button
            disabled={!confirmed || busy}
            className="w-full"
            onClick={() =>
              void run(async () => {
                await api('/mfa/backup-codes/acknowledge', { method: 'POST', realm });
                await queryClient.invalidateQueries({ queryKey: ['profile'] });
              })
            }
          >
            {busy ? 'Finishing…' : 'Continue'}
          </Button>
        </>
      ) : error ? (
        <p className="text-[13px] text-danger">{error}</p>
      ) : (
        <p className="text-[13px] text-muted">Generating your codes…</p>
      )}
    </MfaCard>
  );
}

/* --------------------------------------------------------------- recovery */

/**
 * Vendor-only, unauthenticated, and it resets nothing.
 *
 * The response is identical whether or not the address exists, so this page cannot be used to find
 * out who has an account — which is why it says "if that address belongs to an account" rather than
 * confirming anything.
 */
export function MfaRecovery() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit();

  return (
    <MfaCard
      realm="vendor"
      icon={<ShieldCheck className="size-4" />}
      title="Request MFA recovery"
      intro="An operator resets your second factor by hand, after checking who you are. Tell us which account, and we will pass the request on."
    >
      {sent ? (
        <p className="rounded border border-border bg-inset px-3 py-2.5 text-[13px] leading-relaxed text-muted">
          If that address belongs to an account, an operator has been notified. They will contact you before resetting
          anything.
        </p>
      ) : (
        <form
          className="grid gap-4"
          onSubmit={e => {
            e.preventDefault();
            void run(async () => {
              await api('/mfa/recovery-request', {
                method: 'POST',
                realm: 'vendor',
                body: JSON.stringify({ email })
              });
              setSent(true);
            });
          }}
        >
          <Field label="Work email" htmlFor="email">
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="ops@yourcompany.com"
              autoFocus
              required
            />
          </Field>
          {error ? <p className="text-[13px] text-danger">{error}</p> : null}
          <Button type="submit" disabled={busy || !email} className="w-full">
            {busy ? 'Sending…' : 'Send request'}
          </Button>
        </form>
      )}
    </MfaCard>
  );
}

/* ----------------------------------------------------------- re-enrolment */

/**
 * Moving to a new device, for someone who still has the old factor.
 *
 * Both proofs are required together — a code from the new authenticator and an unused backup code
 * from the current enrolment — because either alone would let whoever is holding a live session
 * swap the second factor for one of their own.
 */
export function MfaReEnrol({ realm }: { realm: Realm }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [enrolment, setEnrolment] = useState<SetupResponse | null>(null);
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const { busy, error, run } = useSubmit();

  if (codes.length)
    return (
      <MfaCard
        realm={realm}
        icon={<KeyRound className="size-4" />}
        title="New backup codes"
        intro="Your old codes no longer work. Save these instead — the previous set was replaced along with the secret."
      >
        <ul className="grid grid-cols-2 gap-x-6 gap-y-2 rounded border border-border bg-inset px-4 py-4 font-mono text-[13px]">
          {codes.map(c => (
            <li key={c} className="tracking-wider">
              {c}
            </li>
          ))}
        </ul>
        <Button className="w-full" onClick={() => navigate(realm === 'admin' ? '/admin' : '/v')}>
          Done
        </Button>
      </MfaCard>
    );

  return (
    <MfaCard
      realm={realm}
      icon={<Smartphone className="size-4" />}
      title="Move to a new authenticator"
      intro="Your current authenticator keeps working until this finishes. If you abandon it halfway, nothing changes."
    >
      {enrolment ? (
        <>
          <div className="grid justify-items-center gap-3 rounded border border-border bg-inset px-4 py-5">
            {qr ? <img src={qr} alt="Enrolment QR code" width={180} height={180} className="rounded bg-white p-2" /> : null}
            <CopyableText value={enrolment.secret} display={enrolment.secret.replace(/(.{4})/g, '$1 ').trim()} />
          </div>
          <form
            className="grid gap-4"
            onSubmit={e => {
              e.preventDefault();
              void run(async () => {
                const result = await api<{ backup_codes: string[] }>('/mfa/reenroll/confirm', {
                  method: 'POST',
                  realm,
                  body: JSON.stringify({ code, backup_code: backupCode })
                });
                setCodes(result.backup_codes);
                await queryClient.invalidateQueries({ queryKey: ['profile'] });
              });
            }}
          >
            <CodeField value={code} onChange={setCode} label="Code from the new authenticator" autoFocus />
            <Field label="A current backup code" htmlFor="backup">
              <Input
                id="backup"
                value={backupCode}
                onChange={e => setBackupCode(e.target.value.trim())}
                className="text-center font-mono tracking-[0.2em]"
                required
              />
            </Field>
            {error ? <p className="text-[13px] text-danger">{error}</p> : null}
            <Button type="submit" disabled={busy || !code || !backupCode} className="w-full">
              {busy ? 'Switching…' : 'Switch authenticator'}
            </Button>
          </form>
        </>
      ) : (
        <>
          {error ? <p className="text-[13px] text-danger">{error}</p> : null}
          <Button
            className="w-full"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const result = await api<SetupResponse>('/mfa/reenroll/start', { method: 'POST', realm });
                setEnrolment(result);
                setQr(await QRCode.toDataURL(result.provisioning_uri, { margin: 1, width: 320 }));
              })
            }
          >
            {busy ? 'Preparing…' : 'Start'}
          </Button>
        </>
      )}
    </MfaCard>
  );
}
